import { MarketChart } from './market-chart';
import { InteractWatchIcon } from '@/client/assets';
import { CurrencyView } from '@/client/components/currency-view';
import { BlockLoading } from '@/client/components/fetch-status/block-loading';
import { FollowMarket } from '@/client/components/follow-market';
import { LocalizedText } from '@/client/components/localized-text';
import { OptionButton } from '@/client/components/option-button';
import { TextTooltip } from '@/client/components/text-tooltip';
import { UploadedImage } from '@/client/components/uploaded-image';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { FollowStatus, MarketDetail, MarketStatus, SectionPrediction } from '@/libs/sdk/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { getBinaryOptions, getBinaryProbabilityPercentage } from '../market-card/binary-options';

type MarketChartCardProps = {
  className?: string;
  predictions: SectionPrediction;
};

type ResolvedMarketChartCardProps = {
  className?: string;
  market: MarketDetail;
};

export const ResolvedMarketChartCard = ({ className, market }: ResolvedMarketChartCardProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  // Paired name-then-index resolution: index fallback only fires when NEITHER
  // side name-matches, so yes/no can't collapse to the same option.
  const { yes, no } = useMemo(
    () => getBinaryOptions(market?.optionList ?? [], o => o.optionName),
    [market?.optionList]
  );
  const resultOption = useMemo(
    () => market?.optionList.find(item => item.optionAddress === market?.resultOptionAddress),
    [market?.optionList, market?.resultOptionAddress]
  );

  // Calculate probability percentage
  const probability = useMemo(() => {
    return (
      getBinaryProbabilityPercentage(market?.optionList ?? [], {
        getName: option => option.optionName,
        getPrice: option => option.price,
        getDecimal: option => option.decimal,
      }) ?? 50
    );
  }, [market?.optionList]);

  if (!market || !yes || !no) return null;

  return (
    <div className={cn('mcard-neon mcard-surface group flex flex-col h-full p-4 rounded-xl', className)}>
      {/* Header */}
      <div
        className="flex items-start gap-2.5 cursor-pointer"
        onClick={() => navigate(`/market/${market.marketAddress}`)}
      >
        <div className="shrink-0 size-9 rounded-md overflow-hidden bg-muted">
          <UploadedImage fileKey={market.marketPicUrl} className="size-full object-cover" />
        </div>
        <TextTooltip
          tooltip={<LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>}
          className="flex-1 line-clamp-2 font-medium text-sm leading-snug hover:text-foreground/70 transition-colors"
        >
          <LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>
        </TextTooltip>
      </div>

      {market.marketStatus == MarketStatus.progress && (
        <div className="mt-3 space-y-2.5">
          {/* Probability display */}
          <div className="text-muted-foreground text-xs">
            <span className="text-emerald-600 font-semibold text-base">{probability}%</span>
            <span className="ms-1">{t('chance')}</span>
          </div>

          {/* Yes/No buttons */}
          <div className="flex gap-2">
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={yes.decimal}
              optionAddress={yes.optionAddress}
              optionName={yes.optionName}
              optionSide="yes"
              optionDescription={yes.description}
              optionPrice={Number(yes.price)}
              optionDecimal={yes.decimal}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            />
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={no.decimal}
              optionAddress={no.optionAddress}
              optionName={no.optionName}
              optionSide="no"
              optionDescription={no.description}
              optionPrice={Number(no.price)}
              optionDecimal={no.decimal}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            />
          </div>
        </div>
      )}

      {market.marketStatus >= MarketStatus.settlement && resultOption && (
        <div
          className={cn(
            'mt-3 text-xs font-medium py-1.5 px-2 rounded-md',
            resultOption?.optionAddress === yes.optionAddress ? 'text-emerald-600 bg-emerald-500/10' : 'text-red-500 bg-red-500/10'
          )}
        >
          {t('results-out-label')} {resultOption.description}
        </div>
      )}

      {/* Chart */}
      <div className="flex-1 min-h-0 mt-3">
        <MarketChart market={market} />
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <CurrencyView value={market.volume} decimal={market.decimal} baseTokenAddress={market.baseTokenAddress}>
          {t('vol')}
        </CurrencyView>
        <FollowMarket
          variant="ghost"
          iconClassName="p-0"
          className="size-3.5 hover:text-foreground transition-colors"
          watchIcon={<InteractWatchIcon className="size-3.5 fill-current" />}
          isFollowed={market.isFollowed == FollowStatus.yes}
          address={market.marketAddress}
        />
      </div>
    </div>
  );
};

export const MarketChartCard = ({ className, predictions }: MarketChartCardProps) => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data: market, isLoading } = useMarketDetail(api, { baseTokenAddress, marketAddress: predictions.prediction }, { subscribe: false });

  if (isLoading) return <BlockLoading />;
  if (!market) return null;

  return <ResolvedMarketChartCard className={className} market={market} />;
};
